import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import AnimationEditorHeader from "../../../components/studio/animationEditor/AnimationEditorHeader.jsx";
import EditorDisplay from "../../../components/studio/animationEditor/EditorDisplay.jsx";
import MonacoScriptEditor from "../../../components/studio/animationEditor/MonacoScriptEditor.jsx";
import PresetsPanel from "../../../components/studio/animationEditor/PresetsPanel.jsx";
import useAnimationAssets from "../../../hooks/useAnimationAssets.js";
import { getAnimation } from "../../../services/animation.service.js";
import "../../../styles/studio/animation/animationEditorWorkbench.css";

const MAX_CONSOLE_LOGS = 400;
const LOG_FLUSH_MS = 50;
const DEFAULT_CAMERA_STATE = {
  x: 800,
  y: 450,
  z: 0,
  zoom: 1,
  rotation: 0,
  originX: 800,
  originY: 450,
  xVelocity: 0,
  yVelocity: 0,
  zVelocity: 0,
  xAcceleration: 0,
  yAcceleration: 0,
  zAcceleration: 0,
  zoomVelocity: 0,
  zoomAcceleration: 0,
  rotationVelocity: 0,
  rotationAcceleration: 0,
  time: 0,
};
const DEFAULT_CAMERA_TOOL_VALUES = {
  x: "800",
  y: "450",
  z: "0",
  zoom: "1",
  rotation: "0",
  originX: "800",
  originY: "450",
  xVelocity: "0",
  yVelocity: "0",
  zVelocity: "0",
  xAcceleration: "0",
  yAcceleration: "0",
  zAcceleration: "0",
  zoomVelocity: "0",
  zoomAcceleration: "0",
  rotationVelocity: "0",
  rotationAcceleration: "0",
};

const formatLocationCoordinate = (value) => {
  const rounded = Math.round((Number(value) || 0) * 100) / 100;
  return Number.isInteger(rounded)
    ? String(rounded)
    : rounded.toFixed(2).replace(/\.?0+$/, "");
};

const parseEditorNumber = (value) => {
  const parsed = Number(String(value ?? "").trim());
  return Number.isFinite(parsed) ? parsed : null;
};

const buildCameraToolValues = (cameraState) => ({
  x: formatLocationCoordinate(cameraState?.x ?? DEFAULT_CAMERA_STATE.x),
  y: formatLocationCoordinate(cameraState?.y ?? DEFAULT_CAMERA_STATE.y),
  z: formatLocationCoordinate(cameraState?.z ?? DEFAULT_CAMERA_STATE.z),
  zoom: formatLocationCoordinate(cameraState?.zoom ?? DEFAULT_CAMERA_STATE.zoom),
  rotation: formatLocationCoordinate(
    cameraState?.rotation ?? DEFAULT_CAMERA_STATE.rotation,
  ),
  originX: formatLocationCoordinate(
    cameraState?.originX ?? DEFAULT_CAMERA_STATE.originX,
  ),
  originY: formatLocationCoordinate(
    cameraState?.originY ?? DEFAULT_CAMERA_STATE.originY,
  ),
  xVelocity: formatLocationCoordinate(
    cameraState?.xVelocity ?? DEFAULT_CAMERA_STATE.xVelocity,
  ),
  yVelocity: formatLocationCoordinate(
    cameraState?.yVelocity ?? DEFAULT_CAMERA_STATE.yVelocity,
  ),
  zVelocity: formatLocationCoordinate(
    cameraState?.zVelocity ?? DEFAULT_CAMERA_STATE.zVelocity,
  ),
  xAcceleration: formatLocationCoordinate(
    cameraState?.xAcceleration ?? DEFAULT_CAMERA_STATE.xAcceleration,
  ),
  yAcceleration: formatLocationCoordinate(
    cameraState?.yAcceleration ?? DEFAULT_CAMERA_STATE.yAcceleration,
  ),
  zAcceleration: formatLocationCoordinate(
    cameraState?.zAcceleration ?? DEFAULT_CAMERA_STATE.zAcceleration,
  ),
  zoomVelocity: formatLocationCoordinate(
    cameraState?.zoomVelocity ?? DEFAULT_CAMERA_STATE.zoomVelocity,
  ),
  zoomAcceleration: formatLocationCoordinate(
    cameraState?.zoomAcceleration ?? DEFAULT_CAMERA_STATE.zoomAcceleration,
  ),
  rotationVelocity: formatLocationCoordinate(
    cameraState?.rotationVelocity ?? DEFAULT_CAMERA_STATE.rotationVelocity,
  ),
  rotationAcceleration: formatLocationCoordinate(
    cameraState?.rotationAcceleration ?? DEFAULT_CAMERA_STATE.rotationAcceleration,
  ),
});

const buildCameraOverride = (cameraValues) => {
  const override = {};
  [
    "x",
    "y",
    "z",
    "rotation",
    "originX",
    "originY",
    "xVelocity",
    "yVelocity",
    "zVelocity",
    "xAcceleration",
    "yAcceleration",
    "zAcceleration",
    "zoomVelocity",
    "zoomAcceleration",
    "rotationVelocity",
    "rotationAcceleration",
  ].forEach((field) => {
    const parsed = parseEditorNumber(cameraValues?.[field]);
    if (parsed !== null) override[field] = parsed;
  });

  const zoom = parseEditorNumber(cameraValues?.zoom);
  if (zoom !== null) override.zoom = Math.max(zoom, 0.0001);

  return Object.keys(override).length ? override : null;
};

const NewAnimationEditor = () => {
  const { animationId } = useParams();
  const [animation, setAnimation] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [logs, setLogs] = useState([]);
  const [theme, setTheme] = useState("dark");
  const [pickedLocation, setPickedLocation] = useState({
    x: "800",
    y: "450",
  });
  const [isLocationPicking, setIsLocationPicking] = useState(false);
  const [cameraToolValues, setCameraToolValues] = useState(
    DEFAULT_CAMERA_TOOL_VALUES,
  );
  const [isCameraOverrideActive, setIsCameraOverrideActive] = useState(false);
  const [cameraOverrideAnchorTime, setCameraOverrideAnchorTime] = useState(0);
  const isFetchingRef = useRef(false);
  const [previewData, setPreviewData] = useState(null);
  const [visualSummary, setVisualSummary] = useState(null);
  const logQueueRef = useRef([]);
  const logFlushTimerRef = useRef(null);
  const liveCameraRef = useRef(DEFAULT_CAMERA_STATE);
  const pendingInitialCameraSyncRef = useRef(true);
  const insertScriptAtCursorRef = useRef(null);
  const pendingScriptInsertRef = useRef("");
  const [currentScriptText, setCurrentScriptText] = useState("");

  // const assets = useAnimationAssets(animationId);
  const {
    assets,
    uploading,
    loadingAssets,
    pendingUpload, // ← new
    recording,
    recordingTime,
    stageAudioFile, // ← renamed from addAudioFile
    stageImageFile, // ← renamed from addImageFile
    stageVideoFile, // ← renamed from addVideoFile
    confirmUpload, // ← new
    cancelPendingUpload, // ← new
    deleteAsset,
    renameAsset,
    startRecording,
    pauseRecording,
    resumeRecording,
    stopRecording,
    trimAssetFile,
    trimmingId,
  } = useAnimationAssets(animationId);

  const buildLogSignature = (entry) =>
    `${entry.type}|${entry.message}|${entry.meta?.line ?? ""}|${entry.meta?.char ?? ""}`;

  const flushQueuedLogs = useCallback(() => {
    logFlushTimerRef.current = null;
    if (logQueueRef.current.length === 0) return;

    const now = new Date();
    const stamp = now.toLocaleTimeString();
    const groupedBySignature = new Map();

    for (const queued of logQueueRef.current) {
      const signature = buildLogSignature(queued);
      const existing = groupedBySignature.get(signature);
      if (existing) {
        existing.count += 1;
        existing.meta = queued.meta;
      } else {
        groupedBySignature.set(signature, {
          ...queued,
          count: 1,
          stamp,
        });
      }
    }
    logQueueRef.current = [];

    const groupedLogs = Array.from(groupedBySignature.values()).map((entry) => ({
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      ...entry,
    }));

    setLogs((prev) => {
      const next = [...groupedLogs, ...prev];
      if (next.length > MAX_CONSOLE_LOGS) {
        return next.slice(0, MAX_CONSOLE_LOGS);
      }
      return next;
    });
  }, []);

  const pushLog = useCallback(
    (type, message, meta = {}) => {
      logQueueRef.current.push({ type, message, meta });
      if (logFlushTimerRef.current) return;
      logFlushTimerRef.current = window.setTimeout(flushQueuedLogs, LOG_FLUSH_MS);
    },
    [flushQueuedLogs],
  );

  const clearLogs = useCallback(() => {
    logQueueRef.current = [];
    if (logFlushTimerRef.current) {
      clearTimeout(logFlushTimerRef.current);
      logFlushTimerRef.current = null;
    }
    setLogs([]);
  }, []);

  useEffect(() => {
    return () => {
      if (logFlushTimerRef.current) {
        clearTimeout(logFlushTimerRef.current);
      }
    };
  }, []);

  const fetchAnimation = useCallback(async () => {
    if (!animationId || isFetchingRef.current) return;
    isFetchingRef.current = true;
    setIsLoading(true);
    try {
      const data = await getAnimation(animationId);
      setAnimation({ ...data });
    } catch (error) {
      pushLog("error", error.message || "Failed to load animation");
    } finally {
      isFetchingRef.current = false;
      setIsLoading(false);
    }
  }, [animationId, pushLog]);

  useEffect(() => {
    if (animationId) fetchAnimation();
  }, [animationId, fetchAnimation]);

  const displayAnimation = useMemo(() => {
    if (!previewData) return animation;
    return {
      ...animation,
      metadata: {
        ...animation.metadata,
        duration:
          previewData.metadata?.duration ?? animation.metadata?.duration ?? 0,
      },
      camera: previewData.camera ?? animation.camera,
      animationComponents: previewData.animationComponents,
      timelines: previewData.timelines,
      cameraTimelines: previewData.cameraTimelines,
      connectors: previewData.connectors,
      chapters: previewData.chapters,
      audioMarkers: animation.audioMarkers || animation.metadata?.audioMarkers || [],
      lockedPlaybackMarkerId:
        animation.lockedPlaybackMarkerId ||
        animation.metadata?.lockedPlaybackMarkerId ||
        null,
      audioTracks: previewData.audioTracks,
    };
  }, [animation, previewData]);

  const cameraOverride = useMemo(
    () => {
      if (!isCameraOverrideActive) return null;
      const override = buildCameraOverride(cameraToolValues);
      if (!override) return null;
      return {
        ...override,
        time: cameraOverrideAnchorTime,
      };
    },
    [cameraOverrideAnchorTime, cameraToolValues, isCameraOverrideActive],
  );

  useEffect(() => {
    const saved = window.localStorage.getItem("animation_editor_theme");
    if (saved === "light" || saved === "dark") setTheme(saved);
  }, []);

  useEffect(() => {
    pendingInitialCameraSyncRef.current = true;
    liveCameraRef.current = {
      ...DEFAULT_CAMERA_STATE,
      ...(displayAnimation?.camera ?? {}),
      zoom: Math.max(
        Number(displayAnimation?.camera?.zoom ?? DEFAULT_CAMERA_STATE.zoom),
        0.0001,
      ),
      rotation: Number(displayAnimation?.camera?.rotation ?? DEFAULT_CAMERA_STATE.rotation),
      z: Number(displayAnimation?.camera?.z ?? DEFAULT_CAMERA_STATE.z),
      originX: Number(displayAnimation?.camera?.originX ?? DEFAULT_CAMERA_STATE.originX),
      originY: Number(displayAnimation?.camera?.originY ?? DEFAULT_CAMERA_STATE.originY),
      xVelocity: Number(displayAnimation?.camera?.xVelocity ?? DEFAULT_CAMERA_STATE.xVelocity),
      yVelocity: Number(displayAnimation?.camera?.yVelocity ?? DEFAULT_CAMERA_STATE.yVelocity),
      zVelocity: Number(displayAnimation?.camera?.zVelocity ?? DEFAULT_CAMERA_STATE.zVelocity),
      xAcceleration: Number(displayAnimation?.camera?.xAcceleration ?? DEFAULT_CAMERA_STATE.xAcceleration),
      yAcceleration: Number(displayAnimation?.camera?.yAcceleration ?? DEFAULT_CAMERA_STATE.yAcceleration),
      zAcceleration: Number(displayAnimation?.camera?.zAcceleration ?? DEFAULT_CAMERA_STATE.zAcceleration),
      zoomVelocity: Number(displayAnimation?.camera?.zoomVelocity ?? DEFAULT_CAMERA_STATE.zoomVelocity),
      zoomAcceleration: Number(displayAnimation?.camera?.zoomAcceleration ?? DEFAULT_CAMERA_STATE.zoomAcceleration),
      rotationVelocity: Number(displayAnimation?.camera?.rotationVelocity ?? DEFAULT_CAMERA_STATE.rotationVelocity),
      rotationAcceleration: Number(displayAnimation?.camera?.rotationAcceleration ?? DEFAULT_CAMERA_STATE.rotationAcceleration),
      time: 0,
    };

    if (!isCameraOverrideActive) {
      setCameraToolValues(buildCameraToolValues(displayAnimation?.camera));
    }
  }, [
    animationId,
    previewData,
    displayAnimation?.camera?.x,
    displayAnimation?.camera?.y,
    displayAnimation?.camera?.z,
    displayAnimation?.camera?.zoom,
    displayAnimation?.camera?.rotation,
    displayAnimation?.camera?.originX,
    displayAnimation?.camera?.originY,
    displayAnimation?.camera?.xVelocity,
    displayAnimation?.camera?.yVelocity,
    displayAnimation?.camera?.zVelocity,
    displayAnimation?.camera?.xAcceleration,
    displayAnimation?.camera?.yAcceleration,
    displayAnimation?.camera?.zAcceleration,
    displayAnimation?.camera?.zoomVelocity,
    displayAnimation?.camera?.zoomAcceleration,
    displayAnimation?.camera?.rotationVelocity,
    displayAnimation?.camera?.rotationAcceleration,
  ]); // reset tool seed when the active preview payload changes

  const toggleTheme = useCallback(() => {
    setTheme((prev) => {
      const next = prev === "dark" ? "light" : "dark";
      window.localStorage.setItem("animation_editor_theme", next);
      return next;
    });
  }, []);

  const handlePreview = useCallback((payload) => {
    setPreviewData(payload?.previewData || null);
    setVisualSummary(payload?.visualSummary || null);
  }, []);

  const handleLocationInputChange = useCallback((axis, value) => {
    setPickedLocation((prev) => ({
      ...prev,
      [axis]: value,
    }));
  }, []);

  const handleToggleLocationPicking = useCallback(() => {
    setIsLocationPicking((prev) => !prev);
  }, []);

  const handleCancelLocationPicking = useCallback(() => {
    setIsLocationPicking(false);
  }, []);

  const handleLocationPick = useCallback((point) => {
    if (!point) return;

    setPickedLocation({
      x: formatLocationCoordinate(point.x),
      y: formatLocationCoordinate(point.y),
    });
    setIsLocationPicking(false);
  }, []);

  const handleCameraInputChange = useCallback((field, value) => {
    setCameraToolValues((prev) => ({
      ...prev,
      [field]: value,
    }));
    setCameraOverrideAnchorTime(liveCameraRef.current?.time ?? 0);
    setIsCameraOverrideActive(true);
  }, []);

  const handleSyncCameraToLive = useCallback(() => {
    setCameraToolValues(buildCameraToolValues(liveCameraRef.current));
    setCameraOverrideAnchorTime(liveCameraRef.current?.time ?? 0);
    setIsCameraOverrideActive(false);
  }, []);

  const handleResetCameraOverride = useCallback(() => {
    setCameraToolValues(DEFAULT_CAMERA_TOOL_VALUES);
    setCameraOverrideAnchorTime(liveCameraRef.current?.time ?? 0);
    setIsCameraOverrideActive(true);
  }, []);

  const handleCameraStateChange = useCallback(
    (cameraState) => {
      if (!cameraState) return;
      liveCameraRef.current = cameraState;
      if (isCameraOverrideActive) return;

      setCameraToolValues(buildCameraToolValues(cameraState));
    },
    [isCameraOverrideActive],
  );

  const handleRegisterInsertAtCursor = useCallback((handler) => {
    insertScriptAtCursorRef.current =
      typeof handler === "function" ? handler : null;

    if (
      insertScriptAtCursorRef.current &&
      pendingScriptInsertRef.current.trim()
    ) {
      const pendingText = pendingScriptInsertRef.current;
      pendingScriptInsertRef.current = "";
      insertScriptAtCursorRef.current(pendingText);
    }
  }, []);

  const handleInsertMarkerTime = useCallback((timeText) => {
    if (!timeText) return false;
    return insertScriptAtCursorRef.current?.(timeText) ?? false;
  }, []);

  const handleInsertScriptSnippet = useCallback((snippet) => {
    const text = String(snippet || "");
    if (!text.trim()) return false;
    if (insertScriptAtCursorRef.current) {
      return insertScriptAtCursorRef.current(text);
    }
    pendingScriptInsertRef.current = text;
    return true;
  }, []);

  return (
    <main className={`animation-workbench theme-${theme}`}>
      <AnimationEditorHeader
        animationMetadata={animation?.metadata}
        onSaved={fetchAnimation}
        pushLog={pushLog}
        theme={theme}
        onToggleTheme={toggleTheme}
      />

      <section className="animation-workbench-body">
        {/* Left: preview + console */}
        <div className="animation-workbench-left-side">
          <div className="animation-workbench-preview">
            <EditorDisplay
              animation={displayAnimation}
              assets={assets.assets}
              currentScriptText={currentScriptText}
              onInsertMarkerTime={handleInsertMarkerTime}
              onInsertScriptSnippet={handleInsertScriptSnippet}
              location={pickedLocation}
              isLocationPicking={isLocationPicking}
              onLocationInputChange={handleLocationInputChange}
              onToggleLocationPicking={handleToggleLocationPicking}
              onLocationPick={handleLocationPick}
              onCancelLocationPick={handleCancelLocationPicking}
              toolCamera={cameraToolValues}
              isCameraOverrideActive={isCameraOverrideActive}
              onCameraInputChange={handleCameraInputChange}
              onSyncCameraToLive={handleSyncCameraToLive}
              onResetCameraOverride={handleResetCameraOverride}
              cameraOverride={cameraOverride}
              onCameraStateChange={handleCameraStateChange}
            />
          </div>
        </div>

        {/* Middle: script editor */}
        <div className="animation-workbench-script-side">
          <MonacoScriptEditor
            animation={animation}
            onScriptChange={setCurrentScriptText}
            onExecuted={fetchAnimation}
            pushLog={pushLog}
            onClearLogs={clearLogs}
            onPreview={handlePreview}
            animationId={animationId}
            logs={logs}
            visualSummary={visualSummary}
            registerInsertAtCursor={handleRegisterInsertAtCursor}
          />
        </div>

        {/* Right: presets panel */}
        <div className="animation-workbench-presets-side">
          <PresetsPanel
            assets={assets}
            uploading={uploading}
            loadingAssets={loadingAssets}
            pendingUpload={pendingUpload} // ← new
            recording={recording}
            recordingTime={recordingTime}
            onAddAudio={stageAudioFile} // ← stages, doesn't upload
            onAddImage={stageImageFile}
            onAddVideo={stageVideoFile}
            onConfirmUpload={confirmUpload} // ← new: called with final name
            onCancelUpload={cancelPendingUpload} // ← new: dismisses pending
            onDelete={deleteAsset}
            onRename={renameAsset}
            onStartRecording={startRecording}
            onPauseRecording={pauseRecording}
            onResumeRecording={resumeRecording}
            onStopRecording={stopRecording}
            onTrim={trimAssetFile}
            trimmingId={trimmingId}
          />
        </div>
      </section>
    </main>
  );
};

export default NewAnimationEditor;
